import React, { useEffect, useRef, useState } from 'react';
import { api, mutate, uploadFile, downloadFile } from '../shared/api';
import { formatDate } from '../shared/formatting';
import { Crumbs, Tabs, Card as UiCard, Btn, Pill, DataTable, ui, type Col } from '../shared/ui';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';

const isHrmsTheme = () => typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';
const GREEN = '#1f7a36';
const RED = '#b14040';
const AMBER = 'var(--ad-acc-3)';

// ── Tipovi ────────────────────────────────────────────────────────────────────

type MySop = {
    id: number; public_id: string; title: string; description: string | null;
    ai_summary: string | null; pass_threshold_pct: number; retake_interval_months: number | null;
    status: string; last_score: number | null; passed_at: string | null; valid_until: string | null;
};
type ManageSop = {
    id: number; title: string; status: string; failure_note: string | null;
    pass_threshold_pct: number; retake_interval_months: number | null; created_at: string | null;
    assignment_count: number; exam_count: number; attempt_count: number;
    department_id: number | null; department_name: string | null;
};
// email je NULLABLE u bi_users (djelatnici poslovnica se prijavljuju kiosk-PIN-om) → nikad
// ne zovi .toLowerCase() bez ?? ''. login: 'email' | 'pin' | 'none' (none = nedohvatljiv dok mu HR ne izda PIN).
type Pickers = {
    users: Array<{ id: number; name: string | null; email: string | null; login?: 'email' | 'pin' | 'none' }>;
    groups: Array<{ id: number; name: string }>;
    sop_groups?: Array<{ id: number; name: string; member_count: number }>;
    departments?: Array<{ id: number; name: string }>;
};

// Odjeli — spremnik SOP-ova s dozvolom "tko smije vidjeti" (po korisniku i/ili roli).
// Odvojeno od bi_sop_groups (koje su popis polaznika kojima se dodjeljuje obavezno polaganje).
type DepartmentViewer = { id: number; user_id: number | null; user_name: string | null; user_email: string | null; role: string | null };
type SopDepartment = {
    id: number; public_id: string; name: string; description: string | null; created_at: string | null;
    viewers: DepartmentViewer[]; sop_count: number;
};
type SopVersion = {
    id: number; version_number: number; file_name: string | null; file_size: number | null;
    note: string | null; created_at: string | null; uploaded_by_name: string | null;
};

// SOP grupe: polaznici mogu biti registrirani korisnici ILI ljudi bez računa (ručni unos / HR registar).
// Neregistrirani se automatski vežu na račun kad se pojavi korisnik s istim emailom.
type SopGroupMember = {
    id: number; user_id: number | null; employee_id: number | null;
    full_name: string; email: string | null; note: string | null;
    registered: boolean; auto_linked: boolean;
};
type SopGroup = {
    id: number; public_id: string; name: string; description: string | null; created_at: string | null;
    members: SopGroupMember[]; sops: Array<{ id: number; title: string; status: string }>;
    member_count: number; unregistered_count: number;
};
type GroupCandidates = {
    users: Array<{ id: number; name: string | null; email: string | null; login?: 'email' | 'pin' | 'none' }>;
    employees: Array<{ id: number; full_name: string; email: string | null }>;
};
type IndexResp = { my: MySop[]; manage: ManageSop[] | null; pickers: Pickers | null; can_manage: boolean };

type SopDetail = {
    sop: {
        id: number; title: string; description: string | null; status: string; failure_note: string | null;
        file_name: string | null; ai_summary: string | null;
        ai_key_points: Array<{ title: string; text: string }>;
        ai_common_mistakes: string[];
        ai_lesson_html: string | null;
        pass_threshold_pct: number; retake_interval_months: number | null;
        department_id: number | null; department_name: string | null; version: number;
    };
    my_status: { status: string; last_score: number | null; passed_at: string | null; valid_until: string | null };
    my_attempts: Array<{ id: number; score_pct: number; passed: boolean; finished_at: string | null; valid_until: string | null; version_number: number }>;
    admin: null | {
        assignments: Array<{ id: number; user_id: number | null; group_id: number | null; due_at: string | null; user_name: string | null; group_name: string | null }>;
        exams: Array<{ id: number; title: string; version_number: number; is_active: boolean; created_at: string | null; question_count: number }>;
    };
    can_manage: boolean;
};

type ExamResp = { exam_id: number; title: string; version: number; seed?: number; pass_threshold_pct: number; questions: Array<{ q: string; options: string[]; order?: number[] }> };
type GradeResp = {
    attempt_id?: number; certificate_no?: string | null;
    score_pct: number; passed: boolean; threshold: number; correct: number; total: number; valid_until: string | null;
    // correct_index + explain dolaze SAMO kad je položeno (anti-cheat).
    results: Array<{ index: number; your: number | null; ok: boolean; correct_index?: number; explain?: string }>;
};

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { hr: string; en: string; color: string; bg: string; icon: string }> = {
    pending: { hr: 'Čeka polaganje', en: 'Awaiting exam', color: AMBER, bg: 'rgba(138,104,23,0.12)', icon: '🆕' },
    failed: { hr: 'Pokušaj ponovno', en: 'Try again', color: RED, bg: 'rgba(177,64,64,0.12)', icon: '🔁' },
    valid: { hr: 'Položeno', en: 'Certified', color: GREEN, bg: 'rgba(31,122,54,0.12)', icon: '🎓' },
    expiring: { hr: 'Ističe uskoro', en: 'Expiring soon', color: '#c2620a', bg: 'rgba(194,98,10,0.12)', icon: '⚠️' },
    expired: { hr: 'Isteklo — ponovi', en: 'Expired — retake', color: RED, bg: 'rgba(177,64,64,0.15)', icon: '❌' },
    // Član SOP grupe bez korisničkog računa — u grupi je i vodi se u izvještaju, ali ne može polagati.
    unregistered: { hr: 'Nema račun', en: 'No account', color: AMBER, bg: 'rgba(138,104,23,0.12)', icon: '⚠️' },
};

function hrD(iso: string | null | undefined): string {
    if (!iso) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
}

// ── Root modul ────────────────────────────────────────────────────────────────

export function SopModule({ token, locale, userName }: { token: string; locale: Locale; userName: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<IndexResp | null>(null);
    const [tab, setTab] = useState<'my' | 'manage' | 'groups' | 'departments' | 'compliance'>(readUrlParam('tab', 'my') as 'my' | 'manage' | 'groups' | 'departments' | 'compliance');
    useUrlSync('tab', tab, tab === 'my');
    const [openSop, setOpenSop] = useState<number | null>(null);
    const [uploading, setUploading] = useState(false);

    const load = () => api<IndexResp>('/sops', token).then(setData).catch(() => undefined);
    useEffect(() => { void load(); }, [token]);

    const canManage = data?.can_manage ?? false;
    const isHrms = isHrmsTheme();

    const tabDefs = [
        { key: 'my', icon: '📚', label: t('Moji SOP-ovi', 'My SOPs') },
        ...(canManage ? [
            { key: 'manage', icon: '⚙️', label: t('Upravljanje', 'Manage') },
            { key: 'groups', icon: '👥', label: t('Grupe', 'Groups') },
            { key: 'departments', icon: '🏢', label: t('Odjeli', 'Departments') },
            { key: 'compliance', icon: '📊', label: t('Compliance', 'Compliance') },
        ] : []),
    ];

    return (
        <div style={isHrms ? undefined : { maxWidth: 1240, margin: '0 auto' }}>
            {isHrms ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <Crumbs items={[t('Dashboard', 'Dashboard'), t('SOP Akademija', 'SOP Academy')]} />
                        {canManage ? <Btn variant="primary" onClick={() => setUploading(true)}>＋ {t('Učitaj SOP', 'Upload SOP')}</Btn> : null}
                    </div>
                    <Tabs tabs={tabDefs} active={tab} onChange={(k) => setTab(k as typeof tab)} />
                </div>
            ) : (
            <>
            {/* Hero */}
            <div style={{ background: `linear-gradient(135deg, ${BROWN} 0%, ${GOLD} 100%)`, borderRadius: 14, padding: '22px 26px', color: '#fffbeb', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', letterSpacing: '0.02em' }}>🎓 {t('SOP Akademija', 'SOP Academy')}</h2>
                    <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: '0.88rem' }}>
                        {t('Procedure → AI edukacija → ispit → certifikat. Tvoje znanje, dokumentirano.', 'Procedures → AI lesson → exam → certificate. Your knowledge, documented.')}
                    </p>
                </div>
                {canManage ? (
                    <button type="button" onClick={() => setUploading(true)}
                        style={{ background: '#fffbeb', color: BROWN, border: 0, padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: '0.9rem', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                        + {t('Učitaj SOP', 'Upload SOP')}
                    </button>
                ) : null}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '2px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                {([
                    ['my', '📚', t('Moji SOP-ovi', 'My SOPs')],
                    ...(canManage ? [['manage', '⚙️', t('Upravljanje', 'Manage')] as const, ['groups', '👥', t('Grupe', 'Groups')] as const, ['departments', '🏢', t('Odjeli', 'Departments')] as const, ['compliance', '📊', t('Compliance', 'Compliance')] as const] : []),
                ] as Array<readonly [string, string, string]>).map(([id, emoji, label]) => (
                    <button key={id} type="button" onClick={() => setTab(id as typeof tab)}
                        style={{ padding: '8px 16px', border: 0, background: 'transparent', borderBottom: tab === id ? `3px solid ${GOLD}` : '3px solid transparent', color: tab === id ? BROWN : '#999', fontWeight: tab === id ? 700 : 500, cursor: 'pointer', fontSize: '0.9rem' }}>
                        {emoji} {label}
                    </button>
                ))}
            </div>
            </>
            )}

            {tab === 'my' ? <MySops sops={data?.my ?? []} locale={locale} onOpen={(id) => setOpenSop(id)} loaded={data !== null} /> : null}
            {tab === 'manage' && canManage ? <ManagePanel sops={data?.manage ?? []} locale={locale} token={token} pickers={data?.pickers ?? null} onOpen={(id) => setOpenSop(id)} onChanged={() => void load()} /> : null}
            {tab === 'groups' && canManage ? <GroupsPanel token={token} locale={locale} onChanged={() => void load()} /> : null}
            {tab === 'departments' && canManage ? <DepartmentsPanel token={token} locale={locale} onChanged={() => void load()} /> : null}
            {tab === 'compliance' && canManage ? <CompliancePanel token={token} locale={locale} /> : null}

            {uploading ? <UploadModal token={token} locale={locale} pickers={data?.pickers ?? null} onClose={() => setUploading(false)} onDone={() => { setUploading(false); void load(); }} /> : null}
            {openSop !== null ? (
                <SopDrawer sopId={openSop} token={token} locale={locale} userName={userName}
                    pickers={data?.pickers ?? null}
                    onClose={() => { setOpenSop(null); void load(); }} />
            ) : null}
        </div>
    );
}

// ── Moji SOP-ovi (kartice) ────────────────────────────────────────────────────

function MySops({ sops, locale, onOpen, loaded }: { sops: MySop[]; locale: Locale; onOpen: (id: number) => void; loaded: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (!loaded) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (sops.length === 0) {
        return (
            <div style={{ padding: 40, textAlign: 'center', background: 'var(--ad-bg)', borderRadius: 12, border: '1px dashed rgba(var(--ad-soft-rgb),0.4)' }}>
                <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>🎓</div>
                <strong style={{ color: BROWN }}>{t('Još ti nije dodijeljen nijedan SOP.', 'No SOPs assigned to you yet.')}</strong>
                <p className="muted" style={{ fontSize: '0.82rem' }}>{t('Kad ti administrator dodijeli proceduru, pojavit će se ovdje.', 'When an admin assigns you a procedure it will appear here.')}</p>
            </div>
        );
    }
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 14 }}>
            {sops.map((s) => {
                const meta = STATUS_META[s.status] ?? STATUS_META.pending;
                return (
                    <button key={s.id} type="button" onClick={() => onOpen(s.id)}
                        style={{ textAlign: 'left', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: 0, cursor: 'pointer', overflow: 'hidden', boxShadow: '0 1px 6px rgba(107,79,28,0.08)', transition: 'transform 0.12s', display: 'flex', flexDirection: 'column' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}>
                        <div style={{ background: meta.bg, color: meta.color, padding: '6px 14px', fontWeight: 800, fontSize: '0.76rem', letterSpacing: '0.04em' }}>
                            {meta.icon} {locale === 'en' ? meta.en : meta.hr}
                            {s.status === 'valid' && s.valid_until ? ` · ${t('vrijedi do', 'valid until')} ${hrD(s.valid_until)}` : ''}
                            {s.status === 'valid' && !s.valid_until ? ` · ${t('trajno', 'permanent')}` : ''}
                            {(s.status === 'expiring' || s.status === 'expired') && s.valid_until ? ` · ${hrD(s.valid_until)}` : ''}
                        </div>
                        <div style={{ padding: '12px 14px', flex: 1 }}>
                            <strong style={{ color: BROWN, fontSize: '0.96rem', display: 'block', marginBottom: 6 }}>{s.title}</strong>
                            <p className="muted" style={{ fontSize: '0.78rem', margin: 0, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {s.ai_summary ?? s.description ?? ''}
                            </p>
                        </div>
                        <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.74rem', color: '#888' }}>
                            <span>🎯 {t('prag', 'pass')}: {s.pass_threshold_pct}%{s.retake_interval_months ? ` · 🔄 ${s.retake_interval_months} mj` : ''}</span>
                            {s.last_score !== null ? <strong style={{ color: s.last_score >= s.pass_threshold_pct ? GREEN : RED }}>{Math.round(s.last_score)}%</strong> : null}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// ── Upload + AI pipeline animacija ───────────────────────────────────────────

const PIPELINE_STEPS: Array<{ key: string; hr: string; en: string; icon: string }> = [
    { key: 'uploaded', hr: 'Dokument učitan', en: 'Document uploaded', icon: '📄' },
    { key: 'extracting', hr: 'Čitam dokument', en: 'Reading document', icon: '🔍' },
    { key: 'ai_lesson', hr: 'AI piše edukaciju', en: 'AI writing lesson', icon: '🧠' },
    { key: 'ai_exam', hr: 'AI sastavlja ispit', en: 'AI building exam', icon: '🎯' },
    { key: 'ready', hr: 'Spremno!', en: 'Ready!', icon: '✅' },
];

function UploadModal({ token, locale, pickers, onClose, onDone }: { token: string; locale: Locale; pickers: Pickers | null; onClose: () => void; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [departmentId, setDepartmentId] = useState<number | ''>('');
    const [interval, setIntervalM] = useState<number | ''>('');
    const [threshold, setThreshold] = useState(80);
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [phase, setPhase] = useState<'form' | 'processing' | 'failed'>('form');
    const [status, setStatus] = useState<string>('uploaded');
    const [failNote, setFailNote] = useState('');
    const [slow, setSlow] = useState(false); // nakon ~3 min pokaži "traje duže nego obično" + zatvori
    const sopIdRef = useRef<number | null>(null);
    const pollRef = useRef<number | null>(null);
    const pollCountRef = useRef(0);

    useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

    const start = async () => {
        if (!file) { alert(t('Odaberi PDF ili DOCX.', 'Pick a PDF or DOCX.')); return; }
        if (!title.trim()) { alert(t('Naslov je obavezan.', 'Title is required.')); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', title.trim());
        if (description.trim()) fd.append('description', description.trim());
        if (departmentId !== '') fd.append('department_id', String(departmentId));
        if (interval !== '') fd.append('retake_interval_months', String(interval));
        fd.append('pass_threshold_pct', String(threshold));
        setPhase('processing'); setStatus('uploaded');
        try {
            const r = await uploadFile<{ ok: boolean; id: number }>('/sops', token, fd);
            sopIdRef.current = r.id;
            // Fire procesiranje (sinkrono na serveru) + poll status za animaciju.
            // Ako POST padne (422 nečitljiv dokument, 409 već u obradi, timeout proxyja) — ne vrti animaciju
            // do beskonačnosti: provjeri stvarni status pa ili završi, ili pokaži poruku.
            void mutate(`/sops/${r.id}/process`, token, 'POST', {}).catch(async (err: unknown) => {
                const msg = err instanceof Error ? err.message : 'Obrada nije uspjela.';
                try {
                    const d = await api<SopDetail>(`/sops/${r.id}`, token);
                    if (d.sop.status === 'ready') { if (pollRef.current) window.clearInterval(pollRef.current); onDone(); return; }
                    if (d.sop.status === 'extracting' || d.sop.status === 'ai_lesson' || d.sop.status === 'ai_exam') return; // radi dalje, poll preuzima
                    if (pollRef.current) window.clearInterval(pollRef.current);
                    setFailNote(d.sop.failure_note || msg);
                } catch {
                    if (pollRef.current) window.clearInterval(pollRef.current);
                    setFailNote(msg);
                }
                setPhase('failed');
            });
            pollRef.current = window.setInterval(async () => {
                pollCountRef.current += 1;
                if (pollCountRef.current > 72) setSlow(true); // ~3 min (72 × 2.5s)
                try {
                    const d = await api<SopDetail>(`/sops/${r.id}`, token);
                    setStatus(d.sop.status);
                    if (d.sop.status === 'ready') {
                        if (pollRef.current) window.clearInterval(pollRef.current);
                        window.setTimeout(onDone, 900);
                    }
                    if (d.sop.status === 'failed') {
                        if (pollRef.current) window.clearInterval(pollRef.current);
                        setFailNote(d.sop.failure_note ?? '');
                        setPhase('failed');
                    }
                } catch { /* ignore poll errors */ }
            }, 2500);
        } catch (e) {
            setPhase('form');
            alert(e instanceof Error ? e.message : 'API');
        }
    };

    const stepIdx = PIPELINE_STEPS.findIndex((s) => s.key === status);

    return (
        <div onClick={phase === 'form' ? onClose : undefined} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: 'min(640px, 96vw)', maxHeight: '90vh', overflowY: 'auto' }}>
                {phase === 'form' ? (
                    <>
                        <h3 style={{ marginTop: 0, color: BROWN }}>📄 {t('Učitaj novi SOP', 'Upload new SOP')}</h3>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
                            style={{ border: `2px dashed ${dragOver ? GOLD : 'rgba(var(--ad-soft-rgb),0.5)'}`, background: dragOver ? 'rgba(var(--ad-acc-rgb),0.08)' : 'var(--ad-bg)', borderRadius: 12, padding: 26, textAlign: 'center', marginBottom: 14, transition: 'all 0.15s' }}>
                            <div style={{ fontSize: '2rem' }}>{file ? '📎' : '📥'}</div>
                            {file ? (
                                <div style={{ marginTop: 6 }}><strong style={{ color: BROWN }}>{file.name}</strong> <span className="muted">({Math.round(file.size / 1024)} KB)</span></div>
                            ) : (
                                <div style={{ marginTop: 6, fontSize: '0.86rem', color: '#888' }}>{t('Povuci PDF/DOCX ovdje ili', 'Drag PDF/DOCX here or')}</div>
                            )}
                            <label style={{ display: 'inline-block', marginTop: 8, background: BROWN, color: '#fff', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                                {file ? t('Promijeni datoteku', 'Change file') : t('Odaberi datoteku', 'Choose file')}
                                <input type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
                            </label>
                        </div>
                        <label style={{ display: 'block', marginBottom: 10 }}><span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Naslov SOP-a', 'SOP title')}</span>
                            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('npr. Postupak zaprimanja zlata u poslovnici', 'e.g. Gold intake procedure')} style={{ width: '100%', padding: 8, marginTop: 3, borderRadius: 6, border: '1px solid var(--ad-acc)' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}><span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Opis (opcionalno)', 'Description (optional)')}</span>
                            <input value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: '100%', padding: 8, marginTop: 3, borderRadius: 6, border: '1px solid var(--ad-acc)' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}><span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>🏢 {t('Odjel (opcionalno)', 'Department (optional)')}</span>
                            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))} style={{ width: '100%', padding: 8, marginTop: 3, borderRadius: 6, border: '1px solid var(--ad-acc)' }}>
                                <option value="">{t('— bez odjela —', '— none —')}</option>
                                {(pickers?.departments ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </label>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                            <label style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>🔄 {t('Ponovno polaganje', 'Re-certification')}</span>
                                <select value={interval} onChange={(e) => setIntervalM(e.target.value === '' ? '' : Number(e.target.value))} style={{ padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }}>
                                    <option value="">{t('— bez ponavljanja —', '— none —')}</option>
                                    <option value={3}>{t('svaka 3 mjeseca', 'every 3 months')}</option>
                                    <option value={6}>{t('svakih 6 mjeseci', 'every 6 months')}</option>
                                    <option value={12}>{t('svakih 12 mjeseci', 'every 12 months')}</option>
                                    <option value={24}>{t('svaka 24 mjeseca', 'every 24 months')}</option>
                                </select>
                            </label>
                            <label style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>🎯 {t('Prag prolaza', 'Pass threshold')}</span>
                                <select value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} style={{ padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }}>
                                    {[60, 70, 80, 90, 100].map((p) => <option key={p} value={p}>{p}%</option>)}
                                </select>
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={onClose} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                            <button type="button" onClick={() => void start()} style={{ background: GREEN, color: '#fff', border: 0, padding: '9px 20px', borderRadius: 7, fontWeight: 800, cursor: 'pointer' }}>
                                🚀 {t('Učitaj i pokreni AI', 'Upload & run AI')}
                            </button>
                        </div>
                    </>
                ) : phase === 'processing' ? (
                    <div style={{ padding: '10px 6px' }}>
                        <h3 style={{ marginTop: 0, color: BROWN, textAlign: 'center' }}>🤖 {t('AI obrađuje SOP…', 'AI processing SOP…')}</h3>
                        <div style={{ margin: '22px 0' }}>
                            {PIPELINE_STEPS.map((s, i) => {
                                const done = i < stepIdx || status === 'ready';
                                const active = i === stepIdx && status !== 'ready';
                                return (
                                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px', opacity: done || active ? 1 : 0.35, transition: 'opacity 0.4s' }}>
                                        <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', background: done ? 'rgba(31,122,54,0.12)' : active ? 'rgba(var(--ad-acc-rgb),0.18)' : '#f3f3f3', border: `2px solid ${done ? GREEN : active ? GOLD : '#ddd'}`, animation: active ? 'pulse 1.2s infinite' : undefined }}>
                                            {done ? '✓' : s.icon}
                                        </div>
                                        <div>
                                            <strong style={{ color: done ? GREEN : active ? BROWN : '#999', fontSize: '0.9rem' }}>{locale === 'en' ? s.en : s.hr}</strong>
                                            {active ? <span style={{ marginLeft: 8, color: GOLD, fontSize: '0.8rem' }}>●●●</span> : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="muted" style={{ textAlign: 'center', fontSize: '0.76rem' }}>{t('Ovo traje 30-90 sekundi. Možeš pratiti napredak ovdje.', 'Takes 30-90 seconds. Watch progress here.')}</p>
                        {slow ? (
                            <div style={{ textAlign: 'center', marginTop: 8 }}>
                                <p style={{ fontSize: '0.78rem', color: AMBER }}>{t('Traje duže nego obično. Možeš zatvoriti — obrada se nastavlja u pozadini, a SOP će se pojaviti u "Upravljanje" kad bude gotov (ili "Greška" za ponovni pokušaj).', 'Taking longer than usual. You can close — processing continues; the SOP appears in "Manage" when ready (or "Failed" to retry).')}</p>
                                <button type="button" onClick={() => { if (pollRef.current) window.clearInterval(pollRef.current); onDone(); }} className="secondary compact">{t('Zatvori i nastavi u pozadini', 'Close & continue in background')}</button>
                            </div>
                        ) : null}
                        <style>{'@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.12); } }'}</style>
                    </div>
                ) : (
                    <div style={{ padding: '10px 6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2.4rem' }}>😞</div>
                        <h3 style={{ color: RED }}>{t('Obrada nije uspjela', 'Processing failed')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem' }}>{failNote}</p>
                        <button type="button" onClick={onClose} className="secondary compact">{t('Zatvori', 'Close')}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── SOP Drawer (lekcija + ispit + admin) ─────────────────────────────────────

function SopDrawer({ sopId, token, locale, userName, pickers, onClose }: { sopId: number; token: string; locale: Locale; userName: string; pickers: Pickers | null; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [detail, setDetail] = useState<SopDetail | null>(null);
    const [examOpen, setExamOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [versions, setVersions] = useState<SopVersion[] | null>(null);
    const [versionUploading, setVersionUploading] = useState(false);

    const load = () => api<SopDetail>(`/sops/${sopId}`, token).then(setDetail).catch(() => undefined);
    const loadVersions = () => api<{ versions: SopVersion[] }>(`/sops/${sopId}/versions`, token).then((r) => setVersions(r.versions)).catch(() => undefined);
    useEffect(() => { void load(); void loadVersions(); }, [sopId]);

    const s = detail?.sop;
    const meta = detail ? (STATUS_META[detail.my_status.status] ?? STATUS_META.pending) : null;

    const newExam = async () => {
        if (!confirm(t('Generirati NOVI ispit (svježa pitanja, stara verzija se arhivira)?', 'Generate a NEW exam (fresh questions; old version archived)?'))) return;
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; version: number; question_count: number }>(`/sops/${sopId}/generate-exam`, token, 'POST', {});
            alert(t(`Novi ispit v${r.version} — ${r.question_count} pitanja.`, `New exam v${r.version} — ${r.question_count} questions.`));
            void load();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    return (
        <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(860px, 96vw)', height: '100vh', overflowY: 'auto', background: '#fffdf6' }}>
                <div style={{ background: `linear-gradient(135deg, ${BROWN}, ${GOLD})`, padding: '16px 22px', color: '#fffbeb', position: 'sticky', top: 0, zIndex: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.16em', opacity: 0.75 }}>SOP #{sopId}</p>
                        <h2 style={{ margin: '0.15rem 0 0', fontSize: '1.18rem' }}>{s?.title ?? '…'}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '1.05rem' }}>✕</button>
                </div>

                {!detail || !s ? <p className="muted" style={{ padding: 20 }}>{t('Učitavam…', 'Loading…')}</p> : (
                    <div style={{ padding: '16px 24px' }}>
                        {/* Status + CTA */}
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                            {meta ? (
                                <span style={{ background: meta.bg, color: meta.color, padding: '6px 14px', borderRadius: 9, fontWeight: 800, fontSize: '0.84rem' }}>
                                    {meta.icon} {locale === 'en' ? meta.en : meta.hr}
                                    {detail.my_status.valid_until ? ` · ${t('do', 'until')} ${hrD(detail.my_status.valid_until)}` : ''}
                                </span>
                            ) : null}
                            {detail.my_status.last_score !== null ? (
                                <span className="muted" style={{ fontSize: '0.8rem' }}>{t('zadnji rezultat', 'last score')}: <strong>{Math.round(detail.my_status.last_score)}%</strong></span>
                            ) : null}
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                {s.status === 'ready' ? (
                                    <button type="button" onClick={() => setExamOpen(true)}
                                        style={{ background: GREEN, color: '#fff', border: 0, padding: '9px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: '0.88rem', boxShadow: '0 2px 8px rgba(31,122,54,0.3)' }}>
                                        🎯 {t('Kreni na ispit', 'Take the exam')}
                                    </button>
                                ) : null}
                                {detail.can_manage ? (
                                    <>
                                        <button type="button" disabled={busy} onClick={() => setAssignOpen(true)} className="secondary compact" style={{ fontSize: '0.8rem' }}>👥 {t('Dodijeli', 'Assign')}</button>
                                        <button type="button" disabled={busy} onClick={() => void newExam()} className="secondary compact" style={{ fontSize: '0.8rem' }}>✨ {t('Kreiraj novi ispit', 'Create new exam')}</button>
                                    </>
                                ) : null}
                            </span>
                        </div>

                        {/* AI sažetak */}
                        {s.ai_summary ? (
                            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: '0.92rem', lineHeight: 1.55 }}>
                                <strong style={{ color: BROWN, display: 'block', marginBottom: 4, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>🧠 {t('AI sažetak', 'AI summary')}</strong>
                                {s.ai_summary}
                            </div>
                        ) : null}

                        {/* Ključni koraci */}
                        {s.ai_key_points.length > 0 ? (
                            <div style={{ marginBottom: 16 }}>
                                <strong style={{ color: BROWN, display: 'block', marginBottom: 8, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>🪜 {t('Ključni koraci', 'Key steps')}</strong>
                                {s.ai_key_points.map((kp, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: i < s.ai_key_points.length - 1 ? '1px dashed rgba(var(--ad-soft-rgb),0.3)' : 'none' }}>
                                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: `linear-gradient(135deg, ${BROWN}, ${GOLD})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.84rem', flexShrink: 0 }}>{i + 1}</div>
                                        <div>
                                            <strong style={{ fontSize: '0.88rem', color: '#333' }}>{kp.title}</strong>
                                            <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.82rem', lineHeight: 1.5 }}>{kp.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {/* Česte greške */}
                        {s.ai_common_mistakes.length > 0 ? (
                            <div style={{ background: 'rgba(177,64,64,0.06)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                                <strong style={{ color: RED, display: 'block', marginBottom: 6, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>⚠️ {t('Česte greške', 'Common mistakes')}</strong>
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.84rem', lineHeight: 1.6 }}>
                                    {s.ai_common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                            </div>
                        ) : null}

                        {/* Puna lekcija */}
                        {s.ai_lesson_html ? (
                            <details style={{ marginBottom: 16 }} open>
                                <summary style={{ cursor: 'pointer', color: BROWN, fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>📚 {t('Puna edukacija', 'Full lesson')}</summary>
                                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: '6px 20px 14px', fontSize: '0.9rem', lineHeight: 1.6 }}
                                    dangerouslySetInnerHTML={{ __html: s.ai_lesson_html }} />
                            </details>
                        ) : null}

                        {/* Moji pokušaji */}
                        {detail.my_attempts.length > 0 ? (
                            <div style={{ marginBottom: 16 }}>
                                <strong style={{ color: BROWN, display: 'block', marginBottom: 6, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>📈 {t('Moji pokušaji', 'My attempts')}</strong>
                                {detail.my_attempts.map((a) => (
                                    <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.82rem', padding: '4px 0' }}>
                                        <strong style={{ color: a.passed ? GREEN : RED, width: 52 }}>{Math.round(a.score_pct)}%</strong>
                                        <span>{a.passed ? '✅' : '❌'}</span>
                                        <span className="muted">{a.finished_at ? formatDate(a.finished_at, locale) : ''} · {t('ispit', 'exam')} v{a.version_number}</span>
                                        {a.valid_until ? <span className="muted">→ {t('vrijedi do', 'valid until')} {hrD(a.valid_until)}</span> : null}
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {/* Verzije */}
                        <div style={{ borderTop: '2px solid rgba(var(--ad-soft-rgb),0.3)', paddingTop: 14, marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <strong style={{ color: BROWN, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>🗂️ {t('Verzije dokumenta', 'Document versions')}</strong>
                                {detail.can_manage ? (
                                    <button type="button" onClick={() => setVersionUploading(true)} className="secondary compact" style={{ fontSize: '0.76rem' }}>
                                        ⬆️ {t('Nova verzija', 'New version')}
                                    </button>
                                ) : null}
                            </div>
                            {versions === null ? (
                                <p className="muted" style={{ fontSize: '0.8rem' }}>{t('Učitavam…', 'Loading…')}</p>
                            ) : versions.length === 0 ? (
                                <p className="muted" style={{ fontSize: '0.8rem' }}>{t('Nema zapisanih verzija.', 'No versions recorded.')}</p>
                            ) : (
                                versions.map((v) => (
                                    <div key={v.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.82rem', padding: '5px 0', borderBottom: '1px dashed rgba(var(--ad-soft-rgb),0.25)' }}>
                                        <strong style={{ color: BROWN, width: 34 }}>v{v.version_number}</strong>
                                        <span style={{ flex: 1 }}>{v.file_name ?? '—'} {v.note ? <span className="muted">· {v.note}</span> : null}</span>
                                        <span className="muted">{v.created_at ? formatDate(v.created_at, locale) : ''}{v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}</span>
                                        <button type="button" onClick={() => void downloadFile(`/sops/${sopId}/versions/${v.id}/download`, token, v.file_name ?? `sop_v${v.version_number}`)}
                                            style={{ background: 'transparent', border: 0, color: GOLD, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>⬇️</button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Admin: dodjele + ispiti */}
                        {detail.admin ? (
                            <div style={{ borderTop: '2px solid rgba(var(--ad-soft-rgb),0.3)', paddingTop: 14 }}>
                                <strong style={{ color: BROWN, display: 'block', marginBottom: 8, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>⚙️ {t('Administracija', 'Administration')}</strong>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <strong style={{ fontSize: '0.8rem' }}>{t('Dodjele', 'Assignments')} ({detail.admin.assignments.length})</strong>
                                        {detail.admin.assignments.map((a) => (
                                            <div key={a.id} style={{ fontSize: '0.78rem', padding: '3px 0', display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                                <span>{a.user_name ? `👤 ${a.user_name}` : `👥 ${a.group_name}`}{a.due_at ? ` · rok ${hrD(a.due_at)}` : ''}</span>
                                                <button type="button" onClick={() => { void mutate(`/sops/${sopId}/unassign`, token, 'POST', { assignment_id: a.id }).then(() => void load()); }}
                                                    style={{ background: 'transparent', border: 0, color: RED, cursor: 'pointer', fontSize: '0.78rem' }}>✕</button>
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <strong style={{ fontSize: '0.8rem' }}>{t('Ispiti', 'Exams')} ({detail.admin.exams.length})</strong>
                                        {detail.admin.exams.map((e) => (
                                            <div key={e.id} style={{ fontSize: '0.78rem', padding: '3px 0' }}>
                                                v{e.version_number} · {e.question_count} {t('pitanja', 'questions')} {e.is_active ? <strong style={{ color: GREEN }}>● {t('aktivan', 'active')}</strong> : <span className="muted">arhiv</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            {examOpen && s ? (
                <ExamWizard sopId={sopId} sopTitle={s.title} token={token} locale={locale} userName={userName}
                    onClose={() => { setExamOpen(false); void load(); }} />
            ) : null}
            {assignOpen && pickers ? (
                <AssignModal sopId={sopId} token={token} locale={locale} pickers={pickers}
                    assignedUserIds={new Set((detail?.admin?.assignments ?? []).map((a) => a.user_id).filter((x): x is number => x !== null))}
                    assignedGroupIds={new Set((detail?.admin?.assignments ?? []).map((a) => a.group_id).filter((x): x is number => x !== null))}
                    onClose={() => { setAssignOpen(false); void load(); }} />
            ) : null}
            {versionUploading ? (
                <VersionUploadModal sopId={sopId} token={token} locale={locale}
                    onClose={() => setVersionUploading(false)}
                    onDone={() => { setVersionUploading(false); void load(); void loadVersions(); }} />
            ) : null}
        </div>
    );
}

// ── Nova verzija dokumenta (upload, ne briše staru) ──────────────────────────

function VersionUploadModal({ sopId, token, locale, onClose, onDone }: { sopId: number; token: string; locale: Locale; onClose: () => void; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [file, setFile] = useState<File | null>(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!file) { alert(t('Odaberi PDF ili DOCX.', 'Pick a PDF or DOCX.')); return; }
        const fd = new FormData();
        fd.append('file', file);
        if (note.trim()) fd.append('note', note.trim());
        setBusy(true);
        try {
            await uploadFile(`/sops/${sopId}/versions`, token, fd);
            onDone();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: 'min(480px, 96vw)' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>⬆️ {t('Učitaj novu verziju', 'Upload new version')}</h3>
                <p className="muted" style={{ fontSize: '0.82rem', marginTop: -6 }}>{t('Stara datoteka ostaje sačuvana i dostupna u povijesti verzija.', 'The old file stays saved and available in version history.')}</p>
                <label style={{ display: 'inline-block', marginTop: 8, marginBottom: 12, background: BROWN, color: '#fff', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                    {file ? file.name : t('Odaberi datoteku', 'Choose file')}
                    <input type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
                </label>
                <label style={{ display: 'block', marginBottom: 14 }}><span style={{ fontSize: '0.84rem', fontWeight: 600, color: BROWN }}>{t('Napomena (opcionalno)', 'Note (optional)')}</span>
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('npr. ispravljen odjeljak 3', 'e.g. fixed section 3')} style={{ width: '100%', padding: 8, marginTop: 3, borderRadius: 6, border: '1px solid var(--ad-acc)' }} />
                </label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} className="secondary compact" disabled={busy}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void submit()} style={{ background: GREEN, color: '#fff', border: 0, padding: '9px 20px', borderRadius: 7, fontWeight: 800, cursor: 'pointer' }}>
                        {busy ? t('Šaljem…', 'Uploading…') : t('Učitaj verziju', 'Upload version')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Ispit wizard ──────────────────────────────────────────────────────────────

function ExamWizard({ sopId, sopTitle, token, locale, userName, onClose }: { sopId: number; sopTitle: string; token: string; locale: Locale; userName: string; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [exam, setExam] = useState<ExamResp | null>(null);
    const [err, setErr] = useState('');
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState<Array<number | null>>([]);
    const [grade, setGrade] = useState<GradeResp | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api<ExamResp>(`/sops/${sopId}/exam`, token)
            .then((e) => {
                if (!e.questions || e.questions.length === 0) { setErr('Ispit nema pitanja — javi administratoru da regenerira ispit.'); return; }
                setExam(e); setAnswers(new Array(e.questions.length).fill(null));
            })
            .catch((e) => setErr(e instanceof Error ? e.message : 'API'));
    }, [sopId, token]);

    const submit = async () => {
        if (!exam) return;
        const unanswered = answers.filter((a) => a === null).length;
        if (unanswered > 0 && !confirm(t(`${unanswered} pitanja bez odgovora — svejedno predati?`, `${unanswered} unanswered — submit anyway?`))) return;
        setBusy(true);
        try {
            const g = await mutate<GradeResp>(`/sops/${sopId}/attempts`, token, 'POST', { exam_id: exam.exam_id, answers });
            setGrade(g);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    // Pravi branded certifikat (PDF s brojem certifikata + verifikacijom) — server DomPDF.
    const printCert = () => {
        if (!grade?.attempt_id) return;
        fetch(`/api/sops/attempts/${grade.attempt_id}/certificate`, { headers: { Authorization: 'Bearer ' + token } })
            .then((r) => {
                // Bez ove provjere se HTML greška spremi kao .pdf koji se ne otvara.
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.blob();
            })
            .then((b) => {
                const u = URL.createObjectURL(b); const a = document.createElement('a');
                a.href = u; a.download = `Certifikat-${grade.certificate_no ?? grade.attempt_id}.pdf`; a.click();
                setTimeout(() => URL.revokeObjectURL(u), 4000);
            })
            .catch(() => alert(t('Certifikat se nije mogao preuzeti — pokušaj ponovno ili javi administratoru.', 'Certificate download failed — try again or contact an administrator.')));
    };

    return (
        // stopPropagation NA OBA sloja — ExamWizard je sibling drawer-panela pa bi klik inače
        // bubblao do drawer overlay onClose i zatvorio cijeli drawer (izgubljen ispit u tijeku).
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,4,0.82)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fffdf6', borderRadius: 16, width: 'min(680px, 96vw)', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 50px rgba(0,0,0,0.4)' }}>
                {err ? (
                    <div style={{ padding: 30, textAlign: 'center' }}>
                        <p style={{ color: RED }}>{err}</p>
                        <button type="button" onClick={onClose} className="secondary compact">{t('Zatvori', 'Close')}</button>
                    </div>
                ) : !exam ? (
                    <p className="muted" style={{ padding: 30, textAlign: 'center' }}>{t('Učitavam ispit…', 'Loading exam…')}</p>
                ) : grade ? (
                    // ── REZULTAT ──
                    <div style={{ padding: '28px 30px', textAlign: 'center' }}>
                        {grade.passed ? <div style={{ fontSize: '3rem', animation: 'sopPop 0.6s ease' }}>🎉</div> : <div style={{ fontSize: '3rem' }}>😔</div>}
                        <style>{'@keyframes sopPop { 0% { transform: scale(0.3); } 70% { transform: scale(1.25); } 100% { transform: scale(1); } }'}</style>
                        {/* Kružni gauge */}
                        <div style={{ position: 'relative', width: 150, height: 150, margin: '14px auto' }}>
                            <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                                <circle cx="60" cy="60" r="52" fill="none" stroke="#eee" strokeWidth="10" />
                                <circle cx="60" cy="60" r="52" fill="none" stroke={grade.passed ? GREEN : RED} strokeWidth="10" strokeLinecap="round"
                                    strokeDasharray={`${Math.round(326.7 * grade.score_pct / 100)} 327`} />
                            </svg>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <strong style={{ fontSize: '1.9rem', color: grade.passed ? GREEN : RED }}>{Math.round(grade.score_pct)}%</strong>
                                <span className="muted" style={{ fontSize: '0.7rem' }}>{grade.correct}/{grade.total}</span>
                            </div>
                        </div>
                        <h3 style={{ color: grade.passed ? GREEN : RED, margin: '4px 0 4px' }}>
                            {grade.passed ? t('Čestitamo — položio/la si!', 'Congratulations — you passed!') : t(`Nije prošlo (prag ${grade.threshold}%)`, `Not passed (threshold ${grade.threshold}%)`)}
                        </h3>
                        {grade.passed && grade.valid_until ? (
                            <p className="muted" style={{ fontSize: '0.84rem' }}>📅 {t('Certifikat vrijedi do', 'Certificate valid until')} <strong>{hrD(grade.valid_until)}</strong></p>
                        ) : null}
                        {grade.passed ? (
                            <button type="button" onClick={printCert} style={{ background: `linear-gradient(135deg, ${BROWN}, ${GOLD})`, color: '#fff', border: 0, padding: '9px 22px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, margin: '6px 0 14px' }}>
                                🖨 {t('Ispiši certifikat', 'Print certificate')}
                            </button>
                        ) : null}

                        {/* Pregled pitanja — točne odgovore prikazujemo SAMO ako je položeno (anti-cheat).
                            Na pad: samo koje pitanje je netočno + poticaj na ponovno učenje SOP-a. */}
                        <div style={{ textAlign: 'left', marginTop: 10, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.3)', paddingTop: 12 }}>
                            <strong style={{ color: BROWN, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('Pregled odgovora', 'Answer review')}</strong>
                            {!grade.passed ? (
                                <p className="muted" style={{ fontSize: '0.78rem', fontStyle: 'italic', margin: '4px 0 8px' }}>
                                    💡 {t('Točni odgovori se prikazuju tek nakon prolaza. Pročitaj ponovno edukaciju za netočna pitanja i probaj opet.', 'Correct answers show only after passing. Re-read the lesson for the wrong questions and try again.')}
                                </p>
                            ) : null}
                            {exam.questions.map((q, i) => {
                                const r = grade.results[i];
                                return (
                                    <div key={i} style={{ padding: '8px 0', borderBottom: '1px dashed rgba(var(--ad-soft-rgb),0.25)', fontSize: '0.82rem' }}>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <span>{r?.ok ? '✅' : '❌'}</span>
                                            <strong>{i + 1}. {q.q}</strong>
                                        </div>
                                        {!r?.ok ? (
                                            <div style={{ marginLeft: 24, marginTop: 3, color: '#555' }}>
                                                {/* r.your/correct_index su ORIGINALNI indeksi; opcije prikaza su izmiješane → mapiraj natrag preko order. */}
                                                {r?.your !== null && r?.your !== undefined ? <div style={{ color: RED }}>{t('Tvoj odgovor', 'Your answer')}: {q.options[q.order ? q.order.indexOf(r.your) : r.your] ?? '—'}</div> : <div style={{ color: RED }}>{t('Bez odgovora', 'No answer')}</div>}
                                                {grade.passed && r?.correct_index !== undefined ? <div style={{ color: GREEN }}>{t('Točno', 'Correct')}: {q.options[q.order ? q.order.indexOf(r.correct_index) : r.correct_index] ?? '—'}</div> : null}
                                                {grade.passed && r?.explain ? <div className="muted" style={{ fontStyle: 'italic', marginTop: 2 }}>{r.explain}</div> : null}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                            {!grade.passed ? (
                                <button type="button" onClick={() => { setGrade(null); setIdx(0); setAnswers(new Array(exam.questions.length).fill(null)); }}
                                    style={{ background: AMBER, color: '#fff', border: 0, padding: '8px 18px', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>
                                    🔁 {t('Pokušaj ponovno', 'Try again')}
                                </button>
                            ) : null}
                            <button type="button" onClick={onClose} className="secondary compact">{t('Zatvori', 'Close')}</button>
                        </div>
                    </div>
                ) : (
                    // ── PITANJA (wizard) ──
                    <div style={{ padding: '22px 28px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <strong style={{ color: BROWN, fontSize: '0.84rem' }}>🎯 {exam.title}</strong>
                            <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer', fontSize: '1rem', color: '#999' }}>✕</button>
                        </div>
                        {/* Progress */}
                        <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
                            {exam.questions.map((_, i) => (
                                <button key={i} type="button" onClick={() => setIdx(i)}
                                    style={{ flex: 1, height: 7, borderRadius: 4, border: 0, cursor: 'pointer', background: i === idx ? GOLD : answers[i] !== null ? GREEN : '#e5ddc8', transition: 'background 0.2s' }} />
                            ))}
                        </div>
                        <div style={{ fontSize: '0.74rem', color: '#999', marginBottom: 6 }}>
                            {t('Pitanje', 'Question')} {idx + 1} / {exam.questions.length} · {t('prag prolaza', 'pass threshold')} {exam.pass_threshold_pct}%
                        </div>
                        <h3 style={{ margin: '0 0 16px', color: '#2c2c2c', fontSize: '1.02rem', lineHeight: 1.45 }}>{exam.questions[idx].q}</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {exam.questions[idx].options.map((opt, oi) => {
                                // Opcije su IZMIJEŠANE (anti-cheat); u answers spremamo ORIGINALNI indeks (order[displayed]).
                                const origIdx = exam.questions[idx].order ? exam.questions[idx].order![oi] : oi;
                                const sel = answers[idx] === origIdx;
                                return (
                                    <button key={oi} type="button"
                                        onClick={() => setAnswers((prev) => { const n = [...prev]; n[idx] = origIdx; return n; })}
                                        style={{ textAlign: 'left', padding: '11px 14px', borderRadius: 9, cursor: 'pointer', fontSize: '0.88rem', lineHeight: 1.4, border: sel ? `2px solid ${GOLD}` : '2px solid rgba(var(--ad-soft-rgb),0.25)', background: sel ? 'rgba(var(--ad-acc-rgb),0.12)' : '#fff', fontWeight: sel ? 700 : 400, color: sel ? BROWN : '#333', transition: 'all 0.12s' }}>
                                        <span style={{ display: 'inline-block', width: 22, fontWeight: 800, color: sel ? GOLD : '#bbb' }}>{String.fromCharCode(65 + oi)}</span>
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                            <button type="button" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} className="secondary compact" style={{ opacity: idx === 0 ? 0.4 : 1 }}>← {t('Natrag', 'Back')}</button>
                            {idx < exam.questions.length - 1 ? (
                                <button type="button" onClick={() => setIdx((i) => Math.min(exam.questions.length - 1, i + 1))}
                                    style={{ background: BROWN, color: '#fff', border: 0, padding: '8px 20px', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>{t('Dalje', 'Next')} →</button>
                            ) : (
                                <button type="button" disabled={busy} onClick={() => void submit()}
                                    style={{ background: GREEN, color: '#fff', border: 0, padding: '8px 22px', borderRadius: 7, fontWeight: 800, cursor: busy ? 'wait' : 'pointer' }}>
                                    {busy ? '…' : `📝 ${t('Predaj ispit', 'Submit exam')}`}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Assign modal ──────────────────────────────────────────────────────────────

function AssignModal({ sopId, token, locale, pickers, assignedUserIds, assignedGroupIds, onClose }: { sopId: number; token: string; locale: Locale; pickers: Pickers; assignedUserIds: Set<number>; assignedGroupIds: Set<number>; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [userIds, setUserIds] = useState<Set<number>>(new Set());
    const [groupIds, setGroupIds] = useState<Set<number>>(new Set());
    const [sopGroupIds, setSopGroupIds] = useState<Set<number>>(new Set());
    const [dueDays, setDueDays] = useState<number | ''>('');
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(false);

    const toggle = (set: Set<number>, fn: (s: Set<number>) => void, id: number) => {
        const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); fn(n);
    };

    const save = async () => {
        if (userIds.size === 0 && groupIds.size === 0 && sopGroupIds.size === 0) { alert(t('Odaberi barem jednu grupu ili osobu.', 'Pick at least one group or person.')); return; }
        setBusy(true);
        try {
            await mutate(`/sops/${sopId}/assign`, token, 'POST', {
                user_ids: Array.from(userIds), group_ids: Array.from(groupIds),
                sop_group_ids: Array.from(sopGroupIds),
                due_days: dueDays === '' ? null : dueDays,
            });
            onClose();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    // name/email mogu biti NULL (osoba bez logina) — bez ?? '' ovdje puca cijeli modul na prvi upisani znak.
    const needle = q.trim().toLowerCase();
    const filteredUsers = pickers.users.filter((u) => {
        if (needle === '') return true;
        return (u.name ?? '').toLowerCase().includes(needle) || (u.email ?? '').toLowerCase().includes(needle);
    });

    return (
        <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 18 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(560px, 96vw)', maxHeight: '88vh', overflowY: 'auto' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>👥 {t('Dodijeli SOP', 'Assign SOP')}</h3>
                <div style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: '0.8rem', color: BROWN }}>{t('SOP grupe', 'SOP groups')}</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                        {(pickers.sop_groups ?? []).length === 0 ? (
                            <span className="muted" style={{ fontSize: '0.74rem' }}>{t('Nema SOP grupa — kreiraj ih u tabu "Grupe".', 'No SOP groups yet — create them under the "Groups" tab.')}</span>
                        ) : (pickers.sop_groups ?? []).map((g) => {
                            const on = sopGroupIds.has(g.id);
                            return (
                                <button key={g.id} type="button" onClick={() => toggle(sopGroupIds, setSopGroupIds, g.id)}
                                    style={{ padding: '5px 12px', borderRadius: 13, fontSize: '0.76rem', cursor: 'pointer', border: on ? `1.5px solid ${GOLD}` : '1.5px solid #ddd', background: on ? 'rgba(var(--ad-acc-rgb),0.15)' : '#fff', fontWeight: on ? 700 : 400, color: on ? BROWN : '#666' }}>
                                    👥 {g.name} <span className="muted" style={{ fontSize: '0.68rem' }}>({g.member_count})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <strong style={{ fontSize: '0.8rem', color: BROWN }}>{t('Distribucijske liste (m.8)', 'Distribution lists (m.8)')}</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                        {pickers.groups.map((g) => {
                            const already = assignedGroupIds.has(g.id);
                            return (
                                <button key={g.id} type="button" onClick={() => toggle(groupIds, setGroupIds, g.id)}
                                    title={already ? t('Već dodijeljeno', 'Already assigned') : undefined}
                                    style={{ padding: '4px 11px', borderRadius: 13, fontSize: '0.76rem', cursor: 'pointer', border: groupIds.has(g.id) ? `1.5px solid ${GOLD}` : '1.5px solid #ddd', background: groupIds.has(g.id) ? 'rgba(var(--ad-acc-rgb),0.15)' : '#fff', fontWeight: groupIds.has(g.id) ? 700 : 400, color: groupIds.has(g.id) ? BROWN : '#666' }}>
                                    {already ? '✓ ' : ''}{g.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <strong style={{ fontSize: '0.8rem', color: BROWN }}>{t('Pojedinci', 'Individuals')}</strong>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('🔍 traži…', '🔍 search…')} style={{ width: '100%', padding: 6, margin: '5px 0', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.8rem' }} />
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 6 }}>
                        {filteredUsers.length === 0 ? (
                            <p className="muted" style={{ fontSize: '0.78rem', margin: '6px 4px' }}>{t('Nema korisnika za taj upit.', 'No users match that search.')}</p>
                        ) : filteredUsers.map((u) => (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', padding: '3px 4px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={userIds.has(u.id)} onChange={() => toggle(userIds, setUserIds, u.id)} />
                                <span style={{ fontWeight: assignedUserIds.has(u.id) ? 700 : 400 }}>{assignedUserIds.has(u.id) ? '✓ ' : ''}{u.name ?? `#${u.id}`}</span>
                                {u.login === 'pin' ? (
                                    <span className="muted" style={{ fontSize: '0.7rem' }} title={t('Prijava kiosk-PIN-om (nema email)', 'Signs in with a kiosk PIN (no email)')}>🔑 PIN</span>
                                ) : u.login === 'none' ? (
                                    <span style={{ fontSize: '0.7rem', color: AMBER }} title={t('Nema ni email ni PIN — ne može pristupiti ispitu dok mu HR ne izda PIN', 'Neither email nor PIN — cannot reach the exam until HR issues a PIN')}>⚠ {t('bez pristupa', 'no access')}</span>
                                ) : (
                                    <span className="muted" style={{ fontSize: '0.7rem' }}>{u.email ?? ''}</span>
                                )}
                            </label>
                        ))}
                    </div>
                    <p className="muted" style={{ fontSize: '0.7rem', margin: '4px 2px 0' }}>
                        {t(`Prikazano ${filteredUsers.length} od ${pickers.users.length}. ✓ = već dodijeljeno.`, `Showing ${filteredUsers.length} of ${pickers.users.length}. ✓ = already assigned.`)}
                    </p>
                </div>
                <label style={{ display: 'block', marginBottom: 14, fontSize: '0.82rem' }}>
                    <strong style={{ color: BROWN }}>📅 {t('Rok prvog polaganja', 'First exam deadline')}</strong>
                    <select value={dueDays} onChange={(e) => setDueDays(e.target.value === '' ? '' : Number(e.target.value))} style={{ display: 'block', padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 4 }}>
                        <option value="">{t('— bez roka —', '— no deadline —')}</option>
                        <option value={7}>{t('7 dana', '7 days')}</option>
                        <option value={14}>{t('14 dana', '14 days')}</option>
                        <option value={30}>{t('30 dana', '30 days')}</option>
                    </select>
                </label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void save()} style={{ background: GREEN, color: '#fff', border: 0, padding: '8px 18px', borderRadius: 7, fontWeight: 700, cursor: 'pointer' }}>
                        {t('Dodijeli', 'Assign')} ({userIds.size + groupIds.size + sopGroupIds.size})
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Manage panel ──────────────────────────────────────────────────────────────

const SOP_STATUS_BADGE: Record<string, [string, string, string]> = {
    uploaded: ['📄 Učitano', 'Uploaded', '#888'],
    extracting: ['🔍 Čitam…', 'Extracting…', AMBER],
    ai_lesson: ['🧠 AI lekcija…', 'AI lesson…', AMBER],
    ai_exam: ['🎯 AI ispit…', 'AI exam…', AMBER],
    ready: ['✅ Spremno', 'Ready', GREEN],
    failed: ['❌ Greška', 'Failed', RED],
};

function ManagePanel({ sops, locale, token, pickers, onOpen, onChanged }: { sops: ManageSop[]; locale: Locale; token: string; pickers: Pickers | null; onOpen: (id: number) => void; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const del = async (id: number, title: string) => {
        if (!confirm(t(`Obrisati SOP "${title}"?`, `Delete SOP "${title}"?`))) return;
        try { await mutate(`/sops/${id}`, token, 'DELETE', {}); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };
    const retryProcess = async (id: number) => {
        try { void mutate(`/sops/${id}/process`, token, 'POST', {}); alert(t('Obrada ponovno pokrenuta — osvježi za minutu.', 'Processing restarted — refresh in a minute.')); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };
    const setDept = async (id: number, deptId: number | '') => {
        try { await mutate(`/sops/${id}`, token, 'PATCH', { department_id: deptId === '' ? null : deptId }); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };
    const deptSelect = (s: ManageSop) => (
        <select value={s.department_id ?? ''} onClick={(e) => e.stopPropagation()} onChange={(e) => void setDept(s.id, e.target.value === '' ? '' : Number(e.target.value))}
            style={{ fontSize: '0.76rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--ad-acc)' }}>
            <option value="">{t('— bez odjela —', '— none —')}</option>
            {(pickers?.departments ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
    );

    if (sops.length === 0) return <p className="muted" style={{ padding: 16 }}>{t('Još nema SOP-ova — klikni "+ Učitaj SOP" gore.', 'No SOPs yet — click "+ Upload SOP" above.')}</p>;

    const isHrms = isHrmsTheme();
    const stop = (ev: React.MouseEvent) => ev.stopPropagation();
    const statusPill = (s: ManageSop) => {
        const [hrL, en] = SOP_STATUS_BADGE[s.status] ?? [s.status, s.status, '#888'];
        const tone: 'ok' | 'warn' | 'bad' | 'neutral' = s.status === 'ready' ? 'ok' : s.status === 'failed' ? 'bad' : s.status === 'uploaded' ? 'neutral' : 'warn';
        return <Pill tone={tone}>{locale === 'en' ? en : hrL}</Pill>;
    };
    const cols: Col<ManageSop>[] = [
        { key: 'title', label: t('Naslov', 'Title'), render: (s) => <span style={{ fontWeight: 650, color: ui.ink }}>{s.title}</span> },
        { key: 'department', label: t('Odjel', 'Department'), render: (s) => deptSelect(s) },
        { key: 'status', label: t('Status', 'Status'), render: (s) => statusPill(s) },
        { key: 'assignment_count', label: t('Dodjele', 'Assigned'), align: 'center', render: (s) => s.assignment_count },
        { key: 'exam_count', label: t('Ispiti', 'Exams'), align: 'center', render: (s) => s.exam_count },
        { key: 'attempt_count', label: t('Pokušaji', 'Attempts'), align: 'center', render: (s) => s.attempt_count },
        { key: 'recert', label: t('Recert.', 'Recert.'), render: (s) => s.retake_interval_months ? `🔄 ${s.retake_interval_months} mj` : '—' },
        { key: 'act', label: '', align: 'right', render: (s) => (
            <span onClick={stop} style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                <Btn size="sm" onClick={() => onOpen(s.id)}>{t('Otvori', 'Open')}</Btn>
                {s.status !== 'ready' ? <Btn size="sm" variant="soft" onClick={() => void retryProcess(s.id)}>🔁 AI</Btn> : null}
                <Btn size="sm" variant="danger" onClick={() => void del(s.id, s.title)}>🗑</Btn>
            </span>
        ) },
    ];

    if (isHrms) {
        return (
            <UiCard pad={0}>
                <DataTable cols={cols} rows={sops} onRowClick={(s) => onOpen(s.id)} empty={t('Još nema SOP-ova.', 'No SOPs yet.')} />
            </UiCard>
        );
    }

    return (
        <div className="table-wrap" style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10 }}>
            <table className="admin-user-table" style={{ width: '100%', fontSize: '0.84rem' }}>
                <thead><tr>
                    <th>{t('Naslov', 'Title')}</th>
                    <th>{t('Odjel', 'Department')}</th>
                    <th>{t('Status', 'Status')}</th>
                    <th style={{ textAlign: 'center' }}>{t('Dodjele', 'Assigned')}</th>
                    <th style={{ textAlign: 'center' }}>{t('Ispiti', 'Exams')}</th>
                    <th style={{ textAlign: 'center' }}>{t('Pokušaji', 'Attempts')}</th>
                    <th>{t('Recert.', 'Recert.')}</th>
                    <th></th>
                </tr></thead>
                <tbody>
                    {sops.map((s) => {
                        const [hr, en, color] = SOP_STATUS_BADGE[s.status] ?? [s.status, s.status, '#888'];
                        return (
                            <tr key={s.id}>
                                <td style={{ fontWeight: 600, color: BROWN, cursor: 'pointer' }} onClick={() => onOpen(s.id)}>{s.title}</td>
                                <td>{deptSelect(s)}</td>
                                <td><span style={{ color, fontWeight: 700, fontSize: '0.78rem' }} title={s.failure_note ?? ''}>{locale === 'en' ? en : hr}</span></td>
                                <td style={{ textAlign: 'center' }}>{s.assignment_count}</td>
                                <td style={{ textAlign: 'center' }}>{s.exam_count}</td>
                                <td style={{ textAlign: 'center' }}>{s.attempt_count}</td>
                                <td style={{ fontSize: '0.76rem' }}>{s.retake_interval_months ? `🔄 ${s.retake_interval_months} mj` : '—'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" onClick={() => onOpen(s.id)} className="secondary compact" style={{ fontSize: '0.72rem', marginRight: 4 }}>{t('Otvori', 'Open')}</button>
                                    {/* Retry za 'failed' I za zaglavljene među-statuse (worker umro) — ne samo failed. */}
                                    {s.status !== 'ready' ? <button type="button" onClick={() => void retryProcess(s.id)} className="secondary compact" style={{ fontSize: '0.72rem', marginRight: 4 }} title={t('Ponovno pokreni AI obradu', 'Restart AI processing')}>🔁 AI</button> : null}
                                    <button type="button" onClick={() => void del(s.id, s.title)} style={{ background: 'transparent', border: 0, color: RED, cursor: 'pointer', fontSize: '0.85rem' }}>🗑</button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── Compliance matrica ────────────────────────────────────────────────────────

type ComplianceResp = {
    rows: Array<{ sop_id: number; sop_title: string; user_id: number; user_name: string; status: string; last_score: number | null; valid_until: string | null }>;
    sops: Array<{ id: number; title: string }>;
};

function CompliancePanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ComplianceResp | null>(null);

    useEffect(() => { api<ComplianceResp>('/sops/compliance', token).then(setData).catch(() => undefined); }, [token]);

    if (!data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (data.rows.length === 0) return <p className="muted" style={{ padding: 16 }}>{t('Nema dodjela — dodijeli SOP-ove korisnicima/grupama pa se ovdje pojavljuje matrica.', 'No assignments yet — assign SOPs and the matrix appears here.')}</p>;

    // Pivot: redovi = useri, kolone = sops
    const users = Array.from(new Map(data.rows.map((r) => [r.user_id, r.user_name])).entries());
    const cell = new Map<string, typeof data.rows[number]>();
    data.rows.forEach((r) => cell.set(`${r.user_id}|${r.sop_id}`, r));
    const isHrms = isHrmsTheme();

    const matrix = (
        <div className="table-wrap" style={isHrms ? { overflowX: 'auto' } : { border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, overflowX: 'auto' }}>
            <table className="admin-user-table" style={{ fontSize: '0.78rem', minWidth: 600 }}>
                <thead><tr>
                    <th style={{ position: 'sticky', left: 0, background: '#f6e3b3', zIndex: 2 }}>{t('Djelatnik', 'Employee')}</th>
                    {data.sops.map((s) => <th key={s.id} style={{ maxWidth: 140, fontSize: '0.7rem' }}>{s.title}</th>)}
                </tr></thead>
                <tbody>
                    {users.map(([uid, uname]) => (
                        <tr key={uid}>
                            <td style={{ fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{uname}</td>
                            {data.sops.map((s) => {
                                const c = cell.get(`${uid}|${s.id}`);
                                if (!c) return <td key={s.id} style={{ textAlign: 'center', color: '#ccc' }}>·</td>;
                                const meta = STATUS_META[c.status] ?? STATUS_META.pending;
                                return (
                                    <td key={s.id} style={{ textAlign: 'center', background: meta.bg }} title={`${c.status}${c.valid_until ? ' · do ' + hrD(c.valid_until) : ''}`}>
                                        <span style={{ color: meta.color, fontWeight: 700 }}>
                                            {meta.icon}{c.last_score !== null ? ` ${Math.round(c.last_score)}%` : ''}
                                        </span>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return isHrms ? <UiCard pad={0}>{matrix}</UiCard> : matrix;
}

// ── SOP grupe (polaznici) ─────────────────────────────────────────────────────
// Dvopanelno: lijevo popis grupa, desno grupa u fokusu (članovi + dodavanje + dodijeljeni SOP-ovi).
// Polaznik NE mora imati račun — ručno dodani se vežu po emailu čim račun nastane.

function GroupsPanel({ token, locale, onChanged }: { token: string; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [groups, setGroups] = useState<SopGroup[] | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [cand, setCand] = useState<GroupCandidates | null>(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const r = await api<{ groups: SopGroup[] }>('/sops/groups', token);
        setGroups(r.groups);
        setSelectedId((prev) => (prev !== null && r.groups.some((g) => g.id === prev)) ? prev : (r.groups[0]?.id ?? null));
    };
    useEffect(() => {
        void load().catch(() => setGroups([]));
        api<GroupCandidates>('/sops/group-candidates', token).then(setCand).catch(() => undefined);
    }, [token]);

    const selected = groups?.find((g) => g.id === selectedId) ?? null;

    const createGroup = async () => {
        const name = prompt(t('Naziv nove grupe (npr. "Poslovnice — blagajna"):', 'New group name:'));
        if (!name || !name.trim()) return;
        setBusy(true);
        try {
            const r = await mutate<{ id: number }>('/sops/groups', token, 'POST', { name: name.trim() });
            await load();
            setSelectedId(r.id);
            onChanged();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const renameGroup = async (g: SopGroup) => {
        const name = prompt(t('Naziv grupe:', 'Group name:'), g.name);
        if (name === null) return;
        if (!name.trim()) { alert(t('Naziv ne smije biti prazan.', 'Name cannot be empty.')); return; }
        setBusy(true);
        try { await mutate(`/sops/groups/${g.id}`, token, 'PATCH', { name: name.trim() }); await load(); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const editDescription = async (g: SopGroup) => {
        const d = prompt(t('Opis grupe (kome je namijenjena):', 'Group description:'), g.description ?? '');
        if (d === null) return;
        setBusy(true);
        try { await mutate(`/sops/groups/${g.id}`, token, 'PATCH', { description: d.trim() || null }); await load(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const deleteGroup = async (g: SopGroup) => {
        const warn = g.sops.length > 0
            ? t(`Grupa "${g.name}" ima ${g.sops.length} dodijeljenih SOP-ova. Brisanjem se te dodjele skidaju. Nastaviti?`,
                `Group "${g.name}" has ${g.sops.length} assigned SOPs. Deleting removes those assignments. Continue?`)
            : t(`Obrisati grupu "${g.name}"?`, `Delete group "${g.name}"?`);
        if (!confirm(warn)) return;
        setBusy(true);
        try { await mutate(`/sops/groups/${g.id}`, token, 'DELETE', {}); setSelectedId(null); await load(); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const removeMember = async (g: SopGroup, m: SopGroupMember) => {
        if (!confirm(t(`Ukloniti ${m.full_name} iz grupe?`, `Remove ${m.full_name} from the group?`))) return;
        setBusy(true);
        try { await mutate(`/sops/groups/${g.id}/members/${m.id}`, token, 'DELETE', {}); await load(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    if (groups === null) return <p className="muted" style={{ padding: 16 }}>{t('Učitavam…', 'Loading…')}</p>;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(230px, 300px) 1fr', gap: 18, alignItems: 'start' }}>
            {/* ── Lijevo: popis grupa ── */}
            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: 12 }}>
                <button type="button" disabled={busy} onClick={() => void createGroup()}
                    style={{ width: '100%', background: GREEN, color: '#fff', border: 0, padding: '10px 14px', borderRadius: 9, fontWeight: 800, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 10 }}>
                    ＋ {t('Nova grupa', 'New group')}
                </button>
                {groups.length === 0 ? (
                    <p className="muted" style={{ fontSize: '0.8rem', lineHeight: 1.5, margin: '10px 4px' }}>
                        {t('Još nema grupa. Grupa je popis polaznika kojima se onda pridružuje jedan ili više SOP-ova.', 'No groups yet. A group is a list of people that SOPs are then attached to.')}
                    </p>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {groups.map((g) => {
                        const active = g.id === selectedId;
                        return (
                            <button key={g.id} type="button" onClick={() => setSelectedId(g.id)}
                                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 9, cursor: 'pointer', background: active ? 'rgba(var(--ad-acc-rgb),0.14)' : '#fff', border: active ? `1.5px solid ${GOLD}` : '1.5px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                                <strong style={{ display: 'block', color: BROWN, fontSize: '0.9rem' }}>{g.name}</strong>
                                <span className="muted" style={{ fontSize: '0.72rem' }}>
                                    👤 {g.member_count}
                                    {g.unregistered_count > 0 ? ` · ⚠ ${g.unregistered_count} ${t('bez računa', 'no account')}` : ''}
                                    {g.sops.length > 0 ? ` · 📄 ${g.sops.length}` : ''}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Desno: grupa u fokusu ── */}
            {!selected ? (
                <div style={{ padding: 40, textAlign: 'center', background: 'var(--ad-bg)', borderRadius: 12, border: '1px dashed rgba(var(--ad-soft-rgb),0.4)' }}>
                    <div style={{ fontSize: '2.2rem', marginBottom: 6 }}>👥</div>
                    <strong style={{ color: BROWN }}>{t('Odaberi grupu lijevo ili kreiraj novu.', 'Pick a group on the left or create one.')}</strong>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                                <h3 style={{ margin: 0, color: BROWN, fontSize: '1.16rem' }}>{selected.name}</h3>
                                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem', lineHeight: 1.5 }}>
                                    {selected.description || t('Bez opisa.', 'No description.')}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button type="button" disabled={busy} onClick={() => void renameGroup(selected)} className="secondary compact" style={{ fontSize: '0.76rem' }}>✏️ {t('Preimenuj', 'Rename')}</button>
                                <button type="button" disabled={busy} onClick={() => void editDescription(selected)} className="secondary compact" style={{ fontSize: '0.76rem' }}>📝 {t('Opis', 'Description')}</button>
                                <button type="button" disabled={busy} onClick={() => void deleteGroup(selected)} style={{ background: 'transparent', border: '1px solid rgba(177,64,64,0.5)', color: RED, padding: '4px 11px', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700 }}>🗑 {t('Obriši', 'Delete')}</button>
                            </div>
                        </div>
                        {selected.sops.length > 0 ? (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed rgba(var(--ad-soft-rgb),0.35)' }}>
                                <strong style={{ fontSize: '0.72rem', color: BROWN, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('SOP-ovi ove grupe', 'SOPs for this group')}</strong>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                                    {selected.sops.map((s) => (
                                        <span key={s.id} style={{ background: 'rgba(31,122,54,0.1)', color: GREEN, padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700 }}>📄 {s.title}</span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="muted" style={{ marginTop: 12, fontSize: '0.78rem' }}>
                                {t('Grupi još nije pridružen nijedan SOP — otvori SOP u "Upravljanje" pa "Dodijeli".', 'No SOP attached yet — open a SOP under "Manage" and use "Assign".')}
                            </p>
                        )}
                    </div>

                    <AddMembersCard group={selected} cand={cand} token={token} locale={locale} onDone={() => void load()} />

                    <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
                        <strong style={{ color: BROWN, fontSize: '0.86rem' }}>
                            {t('Polaznici', 'Members')} ({selected.members.length})
                            {selected.unregistered_count > 0 ? <span style={{ color: AMBER, fontWeight: 700 }}> · {selected.unregistered_count} {t('bez računa', 'without an account')}</span> : null}
                        </strong>
                        {selected.members.length === 0 ? (
                            <p className="muted" style={{ fontSize: '0.82rem', marginTop: 8 }}>{t('Grupa je prazna — dodaj polaznike gore.', 'Group is empty — add members above.')}</p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10, marginTop: 12 }}>
                                {selected.members.map((m) => (
                                    <div key={m.id} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, background: m.registered ? '#fff' : 'rgba(138,104,23,0.06)' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <strong style={{ fontSize: '0.86rem', color: BROWN, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.full_name}</strong>
                                            <span className="muted" style={{ fontSize: '0.72rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email ?? t('bez emaila', 'no email')}</span>
                                            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: m.registered ? GREEN : AMBER }}>
                                                {m.registered ? (m.auto_linked ? `✓ ${t('povezan s računom', 'linked to account')}` : `✓ ${t('registriran', 'registered')}`) : `⚠ ${t('nema račun — ne može polagati', 'no account — cannot take the exam')}`}
                                            </span>
                                            {m.note ? <span className="muted" style={{ fontSize: '0.68rem', display: 'block', fontStyle: 'italic' }}>{m.note}</span> : null}
                                        </div>
                                        <button type="button" disabled={busy} onClick={() => void removeMember(selected, m)}
                                            title={t('Ukloni iz grupe', 'Remove from group')}
                                            style={{ background: 'transparent', border: 0, color: RED, cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 }}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── SOP odjeli (tko smije vidjeti odjel) ──────────────────────────────────────
// Dvopanelno kao grupe: lijevo popis odjela, desno odjel u fokusu (viewers po korisniku/roli).
// Dozvola je ADITIVNA — dodaje vidljivost, ne skida postojeću (dodjela/grupa) vidljivost.

function DepartmentsPanel({ token, locale, onChanged }: { token: string; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [departments, setDepartments] = useState<SopDepartment[] | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [pickers, setPickers] = useState<Pickers | null>(null);
    const [busy, setBusy] = useState(false);
    const [addUserId, setAddUserId] = useState<number | ''>('');
    const [addRole, setAddRole] = useState('');

    const load = async () => {
        const r = await api<{ departments: SopDepartment[]; can_manage: boolean }>('/sops/departments', token);
        setDepartments(r.departments);
        setSelectedId((prev) => (prev !== null && r.departments.some((d) => d.id === prev)) ? prev : (r.departments[0]?.id ?? null));
    };
    useEffect(() => {
        void load().catch(() => setDepartments([]));
        api<{ pickers: Pickers }>('/sops', token).then((r) => setPickers(r.pickers)).catch(() => undefined);
    }, [token]);

    const selected = departments?.find((d) => d.id === selectedId) ?? null;

    const createDept = async () => {
        const name = prompt(t('Naziv novog odjela (npr. "Blagajna — poslovnice"):', 'New department name:'));
        if (!name || !name.trim()) return;
        setBusy(true);
        try {
            const r = await mutate<{ id: number }>('/sops/departments', token, 'POST', { name: name.trim() });
            await load();
            setSelectedId(r.id);
            onChanged();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const renameDept = async (d: SopDepartment) => {
        const name = prompt(t('Naziv odjela:', 'Department name:'), d.name);
        if (name === null) return;
        if (!name.trim()) { alert(t('Naziv ne smije biti prazan.', 'Name cannot be empty.')); return; }
        setBusy(true);
        try { await mutate(`/sops/departments/${d.id}`, token, 'PATCH', { name: name.trim() }); await load(); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const editDescription = async (d: SopDepartment) => {
        const desc = prompt(t('Opis odjela:', 'Department description:'), d.description ?? '');
        if (desc === null) return;
        setBusy(true);
        try { await mutate(`/sops/departments/${d.id}`, token, 'PATCH', { description: desc.trim() || null }); await load(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const deleteDept = async (d: SopDepartment) => {
        const warn = d.sop_count > 0
            ? t(`Odjel "${d.name}" ima ${d.sop_count} SOP-ova. Brisanjem odjela ti SOP-ovi ostaju, samo gube oznaku odjela. Nastaviti?`,
                `Department "${d.name}" has ${d.sop_count} SOPs. Deleting the department keeps the SOPs but removes their department tag. Continue?`)
            : t(`Obrisati odjel "${d.name}"?`, `Delete department "${d.name}"?`);
        if (!confirm(warn)) return;
        setBusy(true);
        try { await mutate(`/sops/departments/${d.id}`, token, 'DELETE', {}); setSelectedId(null); await load(); onChanged(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const addViewer = async (d: SopDepartment) => {
        if (addUserId === '' && !addRole.trim()) { alert(t('Odaberi korisnika ili upiši rolu.', 'Pick a user or enter a role.')); return; }
        setBusy(true);
        try {
            await mutate(`/sops/departments/${d.id}/viewers`, token, 'POST', {
                user_ids: addUserId !== '' ? [addUserId] : [],
                roles: addRole.trim() ? [addRole.trim()] : [],
            });
            setAddUserId(''); setAddRole('');
            await load();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const removeViewer = async (d: SopDepartment, v: DepartmentViewer) => {
        if (!confirm(t(`Ukloniti ${v.user_name ?? ('rola: ' + v.role)} s popisa gledatelja?`, `Remove ${v.user_name ?? ('role: ' + v.role)} from viewers?`))) return;
        setBusy(true);
        try { await mutate(`/sops/departments/${d.id}/viewers/${v.id}`, token, 'DELETE', {}); await load(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    if (departments === null) return <p className="muted" style={{ padding: 16 }}>{t('Učitavam…', 'Loading…')}</p>;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(230px, 300px) 1fr', gap: 18, alignItems: 'start' }}>
            {/* ── Lijevo: popis odjela ── */}
            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: 12 }}>
                <button type="button" disabled={busy} onClick={() => void createDept()}
                    style={{ width: '100%', background: GREEN, color: '#fff', border: 0, padding: '10px 14px', borderRadius: 9, fontWeight: 800, cursor: 'pointer', fontSize: '0.86rem', marginBottom: 10 }}>
                    ＋ {t('Novi odjel', 'New department')}
                </button>
                {departments.length === 0 ? (
                    <p className="muted" style={{ fontSize: '0.8rem', lineHeight: 1.5, margin: '10px 4px' }}>
                        {t('Još nema odjela. Odjel grupira SOP-ove i definira tko ih smije vidjeti (dodatno uz postojeće dodjele).', 'No departments yet. A department groups SOPs and defines who may view them (in addition to existing assignments).')}
                    </p>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {departments.map((d) => {
                        const active = d.id === selectedId;
                        return (
                            <button key={d.id} type="button" onClick={() => setSelectedId(d.id)}
                                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 9, cursor: 'pointer', background: active ? 'rgba(var(--ad-acc-rgb),0.14)' : '#fff', border: active ? `1.5px solid ${GOLD}` : '1.5px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                                <strong style={{ display: 'block', color: BROWN, fontSize: '0.9rem' }}>🏢 {d.name}</strong>
                                <span className="muted" style={{ fontSize: '0.72rem' }}>
                                    👁 {d.viewers.length} · 📄 {d.sop_count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Desno: odjel u fokusu ── */}
            {!selected ? (
                <div style={{ padding: 40, textAlign: 'center', background: 'var(--ad-bg)', borderRadius: 12, border: '1px dashed rgba(var(--ad-soft-rgb),0.4)' }}>
                    <div style={{ fontSize: '2.2rem', marginBottom: 6 }}>🏢</div>
                    <strong style={{ color: BROWN }}>{t('Odaberi odjel lijevo ili kreiraj novi.', 'Pick a department on the left or create one.')}</strong>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                                <h3 style={{ margin: 0, color: BROWN, fontSize: '1.16rem' }}>🏢 {selected.name}</h3>
                                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem', lineHeight: 1.5 }}>
                                    {selected.description || t('Bez opisa.', 'No description.')}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button type="button" disabled={busy} onClick={() => void renameDept(selected)} className="secondary compact" style={{ fontSize: '0.76rem' }}>✏️ {t('Preimenuj', 'Rename')}</button>
                                <button type="button" disabled={busy} onClick={() => void editDescription(selected)} className="secondary compact" style={{ fontSize: '0.76rem' }}>📝 {t('Opis', 'Description')}</button>
                                <button type="button" disabled={busy} onClick={() => void deleteDept(selected)} style={{ background: 'transparent', border: '1px solid rgba(177,64,64,0.5)', color: RED, padding: '4px 11px', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700 }}>🗑 {t('Obriši', 'Delete')}</button>
                            </div>
                        </div>
                        <p className="muted" style={{ marginTop: 12, fontSize: '0.78rem' }}>
                            📄 {t(`${selected.sop_count} SOP-ova u ovom odjelu`, `${selected.sop_count} SOPs in this department`)}
                        </p>
                    </div>

                    {/* Dodavanje gledatelja */}
                    <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
                        <strong style={{ color: BROWN, fontSize: '0.86rem', display: 'block', marginBottom: 8 }}>👁 {t('Dodaj gledatelja', 'Add viewer')}</strong>
                        <p className="muted" style={{ fontSize: '0.76rem', marginTop: -4, marginBottom: 10 }}>
                            {t('Dozvola za gledanje ovog odjela — po korisniku ili po roli (npr. "administrator"). Dodatno uz postojeće dodjele, ništa ne skida.', 'View permission for this department — per user or per role (e.g. "administrator"). Additive to existing assignments, removes nothing.')}
                        </p>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <label style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>{t('Korisnik', 'User')}</span>
                                <select value={addUserId} onChange={(e) => setAddUserId(e.target.value === '' ? '' : Number(e.target.value))} style={{ padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, minWidth: 200 }}>
                                    <option value="">{t('— odaberi —', '— pick —')}</option>
                                    {(pickers?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name ?? u.email ?? `#${u.id}`}</option>)}
                                </select>
                            </label>
                            <span className="muted" style={{ fontSize: '0.8rem' }}>{t('ili', 'or')}</span>
                            <label style={{ fontSize: '0.82rem' }}><span style={{ fontWeight: 600, color: BROWN, display: 'block' }}>{t('Rola', 'Role')}</span>
                                <input value={addRole} onChange={(e) => setAddRole(e.target.value)} placeholder={t('npr. administrator', 'e.g. administrator')} style={{ padding: 6, borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3 }} />
                            </label>
                            <button type="button" disabled={busy} onClick={() => void addViewer(selected)}
                                style={{ background: GREEN, color: '#fff', border: 0, padding: '8px 16px', borderRadius: 7, fontWeight: 800, cursor: 'pointer', fontSize: '0.82rem' }}>
                                ＋ {t('Dodaj', 'Add')}
                            </button>
                        </div>
                    </div>

                    <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
                        <strong style={{ color: BROWN, fontSize: '0.86rem' }}>{t('Gledatelji', 'Viewers')} ({selected.viewers.length})</strong>
                        {selected.viewers.length === 0 ? (
                            <p className="muted" style={{ fontSize: '0.82rem', marginTop: 8 }}>{t('Nitko još nema izričitu dozvolu — samo admini vide sve odjele.', 'No one has explicit permission yet — only admins see all departments.')}</p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10, marginTop: 12 }}>
                                {selected.viewers.map((v) => (
                                    <div key={v.id} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <div style={{ minWidth: 0 }}>
                                            {v.user_id !== null ? (
                                                <>
                                                    <strong style={{ fontSize: '0.86rem', color: BROWN, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>👤 {v.user_name ?? `#${v.user_id}`}</strong>
                                                    <span className="muted" style={{ fontSize: '0.72rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.user_email ?? ''}</span>
                                                </>
                                            ) : (
                                                <strong style={{ fontSize: '0.86rem', color: BROWN, display: 'block' }}>🏷 {t('rola', 'role')}: {v.role}</strong>
                                            )}
                                        </div>
                                        <button type="button" disabled={busy} onClick={() => void removeViewer(selected, v)}
                                            title={t('Ukloni', 'Remove')}
                                            style={{ background: 'transparent', border: 0, color: RED, cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 }}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Dodavanje polaznika: registrirani korisnici / djelatnici bez računa / ručni unos. */
function AddMembersCard({ group, cand, token, locale, onDone }: { group: SopGroup; cand: GroupCandidates | null; token: string; locale: Locale; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [mode, setMode] = useState<'users' | 'employees' | 'manual'>('users');
    const [q, setQ] = useState('');
    const [pickedUsers, setPickedUsers] = useState<Set<number>>(new Set());
    const [pickedEmployees, setPickedEmployees] = useState<Set<number>>(new Set());
    const [mName, setMName] = useState('');
    const [mEmail, setMEmail] = useState('');
    const [mNote, setMNote] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => { setPickedUsers(new Set()); setPickedEmployees(new Set()); setQ(''); }, [group.id]);

    const inGroupUserIds = new Set(group.members.map((m) => m.user_id).filter((x): x is number => x !== null));
    const inGroupEmails = new Set(group.members.map((m) => (m.email ?? '').toLowerCase()).filter((x) => x !== ''));
    const needle = q.trim().toLowerCase();

    const users = (cand?.users ?? []).filter((u) => needle === ''
        || (u.name ?? '').toLowerCase().includes(needle) || (u.email ?? '').toLowerCase().includes(needle));
    const employees = (cand?.employees ?? []).filter((e) => needle === ''
        || e.full_name.toLowerCase().includes(needle) || (e.email ?? '').toLowerCase().includes(needle));

    const toggle = (set: Set<number>, fn: (s: Set<number>) => void, id: number) => {
        const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); fn(n);
    };

    const addPicked = async () => {
        if (pickedUsers.size === 0 && pickedEmployees.size === 0) return;
        setBusy(true);
        try {
            const r = await mutate<{ added: number; skipped: number }>(`/sops/groups/${group.id}/members`, token, 'POST', {
                user_ids: Array.from(pickedUsers), employee_ids: Array.from(pickedEmployees),
            });
            setPickedUsers(new Set()); setPickedEmployees(new Set());
            if (r.skipped > 0) alert(t(`Dodano ${r.added}, preskočeno ${r.skipped} (već u grupi).`, `Added ${r.added}, skipped ${r.skipped} (already in group).`));
            onDone();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const addManual = async () => {
        if (!mName.trim()) { alert(t('Ime i prezime su obavezni.', 'Full name is required.')); return; }
        setBusy(true);
        try {
            await mutate(`/sops/groups/${group.id}/members`, token, 'POST', {
                manual: [{ full_name: mName.trim(), email: mEmail.trim() || null, note: mNote.trim() || null }],
            });
            setMName(''); setMEmail(''); setMNote('');
            onDone();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const modeBtn = (key: typeof mode, label: string, count?: number) => (
        <button key={key} type="button" onClick={() => setMode(key)}
            style={{ padding: '7px 15px', borderRadius: 20, cursor: 'pointer', fontSize: '0.8rem', fontWeight: mode === key ? 800 : 500, border: mode === key ? `1.5px solid ${GOLD}` : '1.5px solid rgba(var(--ad-soft-rgb),0.35)', background: mode === key ? 'rgba(var(--ad-acc-rgb),0.14)' : '#fff', color: mode === key ? BROWN : '#777' }}>
            {label}{count !== undefined ? ` (${count})` : ''}
        </button>
    );

    const pickedCount = pickedUsers.size + pickedEmployees.size;

    return (
        <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 12, padding: '16px 20px' }}>
            <strong style={{ color: BROWN, fontSize: '0.86rem' }}>➕ {t('Dodaj polaznike', 'Add members')}</strong>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 12px' }}>
                {modeBtn('users', t('Registrirani', 'Registered'), cand?.users.length)}
                {modeBtn('employees', t('Djelatnici bez računa', 'Staff without accounts'), cand?.employees.length)}
                {modeBtn('manual', t('Ručni unos', 'Manual entry'))}
            </div>

            {mode === 'manual' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'end' }}>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: BROWN }}>{t('Ime i prezime', 'Full name')}
                        <input value={mName} onChange={(e) => setMName(e.target.value)} placeholder={t('npr. Ana Anić', 'e.g. Jane Doe')}
                            style={{ width: '100%', padding: 9, marginTop: 4, borderRadius: 8, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                    </label>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: BROWN }}>{t('Email (nije obavezan)', 'Email (optional)')}
                        <input value={mEmail} onChange={(e) => setMEmail(e.target.value)} placeholder="ime.prezime@aurodomus.org"
                            style={{ width: '100%', padding: 9, marginTop: 4, borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.5)', fontSize: '0.86rem' }} />
                    </label>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: BROWN }}>{t('Napomena', 'Note')}
                        <input value={mNote} onChange={(e) => setMNote(e.target.value)} placeholder={t('npr. sezonac, poslovnica Split', 'e.g. seasonal, Split branch')}
                            style={{ width: '100%', padding: 9, marginTop: 4, borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.5)', fontSize: '0.86rem' }} />
                    </label>
                    <button type="button" disabled={busy} onClick={() => void addManual()}
                        style={{ background: GREEN, color: '#fff', border: 0, padding: '10px 20px', borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: '0.84rem' }}>
                        {t('Dodaj', 'Add')}
                    </button>
                    <p className="muted" style={{ gridColumn: '1 / -1', fontSize: '0.74rem', margin: 0, lineHeight: 1.5 }}>
                        {t('Osoba bez računa ulazi u grupu i vidi se u izvještaju, ali ispit može polagati tek kad dobije pristup. Ako upišeš email, sustav je automatski poveže čim se pojavi korisnički račun s tim emailom.', 'A person without an account joins the group and shows in the report, but can only take the exam once they get access. If you enter an email, they are linked automatically as soon as an account with that email exists.')}
                    </p>
                </div>
            ) : (
                <>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('🔍 traži po imenu ili emailu…', '🔍 search by name or email…')}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.5)', fontSize: '0.86rem', marginBottom: 10 }} />
                    <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8, padding: 2 }}>
                        {mode === 'users' ? users.map((u) => {
                            const already = inGroupUserIds.has(u.id) || (u.email ? inGroupEmails.has(u.email.toLowerCase()) : false);
                            const sel = pickedUsers.has(u.id);
                            return (
                                <button key={u.id} type="button" disabled={already} onClick={() => toggle(pickedUsers, setPickedUsers, u.id)}
                                    style={{ textAlign: 'left', padding: '9px 12px', borderRadius: 9, cursor: already ? 'default' : 'pointer', opacity: already ? 0.5 : 1, border: sel ? `1.5px solid ${GOLD}` : '1.5px solid rgba(var(--ad-soft-rgb),0.3)', background: sel ? 'rgba(var(--ad-acc-rgb),0.14)' : '#fff' }}>
                                    <strong style={{ fontSize: '0.84rem', color: BROWN, display: 'block' }}>{sel ? '✓ ' : ''}{u.name ?? `#${u.id}`}</strong>
                                    <span className="muted" style={{ fontSize: '0.7rem' }}>
                                        {u.login === 'pin' ? `🔑 ${t('PIN prijava', 'PIN sign-in')}` : (u.email ?? '—')}
                                        {already ? ` · ${t('već u grupi', 'already in group')}` : ''}
                                    </span>
                                </button>
                            );
                        }) : employees.map((e) => {
                            const already = e.email ? inGroupEmails.has(e.email.toLowerCase()) : false;
                            const sel = pickedEmployees.has(e.id);
                            return (
                                <button key={e.id} type="button" disabled={already} onClick={() => toggle(pickedEmployees, setPickedEmployees, e.id)}
                                    style={{ textAlign: 'left', padding: '9px 12px', borderRadius: 9, cursor: already ? 'default' : 'pointer', opacity: already ? 0.5 : 1, border: sel ? `1.5px solid ${GOLD}` : '1.5px solid rgba(var(--ad-soft-rgb),0.3)', background: sel ? 'rgba(var(--ad-acc-rgb),0.14)' : '#fff' }}>
                                    <strong style={{ fontSize: '0.84rem', color: BROWN, display: 'block' }}>{sel ? '✓ ' : ''}{e.full_name}</strong>
                                    <span className="muted" style={{ fontSize: '0.7rem' }}>{e.email ?? t('bez emaila', 'no email')}{already ? ` · ${t('već u grupi', 'already in group')}` : ''}</span>
                                </button>
                            );
                        })}
                        {(mode === 'users' ? users.length : employees.length) === 0 ? (
                            <p className="muted" style={{ fontSize: '0.8rem', gridColumn: '1 / -1' }}>{t('Nema pogodaka za taj upit.', 'No matches.')}</p>
                        ) : null}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                        <button type="button" disabled={busy || pickedCount === 0} onClick={() => void addPicked()}
                            style={{ background: pickedCount === 0 ? '#ccc' : GREEN, color: '#fff', border: 0, padding: '10px 22px', borderRadius: 8, fontWeight: 800, cursor: pickedCount === 0 ? 'default' : 'pointer', fontSize: '0.86rem' }}>
                            {t('Dodaj označene', 'Add selected')} ({pickedCount})
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
